import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import type { Paragraph } from "@repo/app-types/helpers/gradeValidationHelpers";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import type { FC } from "react";
import {
  ClickableQuote,
  IncompleteQuote,
  Quote,
  type TranscriptFragmentData,
} from "./clickableQuote.js";
import { QuoteMatchMap } from "./gradeValidationHelpers.js";

interface ParagraphsContainerProps {
  recordings: readonly AccountGradingRecording[];
  paragraphs: readonly Paragraph[];
  hasCompleted: boolean;
  quoteMatches: QuoteMatchMap;
  onQuotePress: (quoteId: string) => void;
}

export const ParagraphsContainer: FC<ParagraphsContainerProps> = (props) => {
  const { recordings, paragraphs, hasCompleted, quoteMatches, onQuotePress } =
    props;

  const renderParagraph = (paragraph: Paragraph, index: number) => {
    switch (paragraph.type) {
      case "text":
        return (
          <p key={index} className="px-2 text-sm leading-relaxed">
            {paragraph.fragments.map((f, index) => {
              switch (f.type) {
                case "plain":
                  return <span key={index}>{f.text}</span>;

                case "green":
                  return (
                    <span
                      key={index}
                      className="text-highlight text-highlight-green"
                      style={{ transitionDelay: `${500 * f.highlightIndex}ms` }}
                    >
                      {f.text}
                    </span>
                  );

                case "red":
                  return (
                    <span
                      key={index}
                      className="text-highlight text-highlight-red"
                      style={{ transitionDelay: `${500 * f.highlightIndex}ms` }}
                    >
                      {f.text}
                    </span>
                  );

                default:
                  assertUnreachable(f);
              }
            })}
          </p>
        );

      case "quote": {
        const matches = QuoteMatchMap.findByQuote(quoteMatches, paragraph.id);
        const firstMatch = matches?.[0];
        if (hasCompleted && !firstMatch) {
          return <Quote key={index}>{paragraph.short}</Quote>;
        }

        // Find the matching transcript fragment metadata.
        const fragmentData = ((): TranscriptFragmentData | undefined => {
          if (!hasCompleted || !firstMatch) {
            return undefined;
          }

          const recording = recordings.find(
            (r) => r.id === firstMatch.recordingId,
          );
          const transcript = recording?.transcript;
          if (!transcript) {
            return undefined;
          }

          const { recordingFragmentIndex } = firstMatch;
          const fragment = transcript.fragments[recordingFragmentIndex];
          if (!fragment) {
            return undefined;
          }

          return { recording, fragment };
        })();

        if (!hasCompleted || !fragmentData) {
          return (
            <IncompleteQuote key={index}>{paragraph.short}</IncompleteQuote>
          );
        }

        return (
          <ClickableQuote
            key={index}
            fragmentData={fragmentData}
            onPress={() => onQuotePress(paragraph.id)}
          >
            {paragraph.short}
          </ClickableQuote>
        );
      }

      default:
        assertUnreachable(paragraph);
    }
  };

  return (
    <>
      {paragraphs.length === 0 ? (
        <div className="-mb-px px-2">
          <Loader style={LoaderStyle.ORBIT} />
        </div>
      ) : (
        <div className="flex flex-col gap-3 text-foreground">
          {paragraphs.map((paragraph, index) => {
            return renderParagraph(paragraph, index);
          })}
        </div>
      )}
    </>
  );
};
